import React, { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getRole, RoleKey } from '../config/roles';
import { useNavigate } from 'react-router-dom';
import {
    McButton,
    McCard,
    McIcon,
    McTopBar
} from '@maersk-global/mds-react-wrapper';

/* ────────────────────── keyframes (injected once) ────────────────────── */
const injectKeyframes = () => {
    if (document.getElementById('landing-keyframes')) return;
    const s = document.createElement('style');
    s.id = 'landing-keyframes';
    s.textContent = `
        @keyframes fadeIn   { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp  { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes float    { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
        .lp-pillar { transition: all 0.25s ease; }
        .lp-pillar:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,36,61,0.08); }
    `;
    document.head.appendChild(s);
};

const LandingPage: React.FC = () => {
    const { login, hasRole } = useAuth();
    useEffect(() => { injectKeyframes(); }, []);
    const navigate = useNavigate();

    const pillars = [
        {
            icon: 'mi-magnifying-glass.svg',
            title: 'Self-Service Queries',
            desc: 'Ask questions in natural language and get answers from operational data.',
            accent: '#E96B10',
            path: '/queries',
        },
        {
            icon: 'mi-robot.svg',
            title: 'AI Agents',
            desc: 'Intelligent assistants that proactively surface insights and alerts.',
            accent: '#0073AB',
            path: '/agents',
            roleKey: 'viewAgents' as RoleKey,
        },
        {
            icon: 'mi-calendar.svg',
            title: 'Scheduled Reports',
            desc: 'Automate recurring reports and keep teams informed without manual work.',
            accent: '#00856A',
            path: '/schedules',
            roleKey: 'viewSchedules' as RoleKey,
        },
        {
            icon: 'mi-globe.svg',
            title: 'Regional Governance',
            desc: 'Manage access and policies per region.',
            accent: '#C05600',
            path: '/regions',
            roleKey: 'viewRegions' as RoleKey,
        },
        {
            icon: 'mi-shield-check.svg',
            title: 'Enterprise Security',
            desc: 'Corporate SSO and role-based access across the platform.',
            accent: '#2D6A4F',
            path: '/developer-docs',
            roleKey: 'viewDevDocs' as RoleKey,
        },
    ];

    return (
        <div className="mds" style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            background: '#FFFFFF',
            overflowX: 'hidden',
        }}>
            {/* ━━━━━━━━━━ TOP BAR ━━━━━━━━━━ */}
            <div style={{
                padding: '20px 40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #F0F1F3',
                background: '#FFFFFF',
            }}>
                <img src="/assets/logo.svg" alt="APM Terminals" style={{ height: 40 }} />
                <span style={{ fontSize: '0.78rem', color: '#A0ADB8', letterSpacing: '0.3px' }}>
                    CDH Enterprise
                </span>
            </div>

            {/* ━━━━━━━━━━ MAIN CONTENT ━━━━━━━━━━ */}
            <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '48px 32px',
                animation: 'fadeIn 0.6s ease-out',
            }}>
                <div style={{ maxWidth: '960px', width: '100%', textAlign: 'center' }}>

                    {/* ── Hero text ── */}
                    <div style={{ marginBottom: '40px', animation: 'slideUp 0.5s ease-out' }}>
                        <h1 style={{
                            fontSize: '2.4rem',
                            fontWeight: 800,
                            color: '#00243D',
                            margin: '0 0 12px 0',
                            letterSpacing: '-0.5px',
                            lineHeight: 1.15,
                        }}>
                            Intelligence for Global
                            <br />
                            Port Operations
                        </h1>
                        <p style={{
                            fontSize: '1.02rem',
                            color: '#6B7B8D',
                            lineHeight: 1.7,
                            maxWidth: '600px',
                            margin: '0 auto',
                        }}>
                            Enabling data-driven decisions across our global network.
                            CDH Enterprise brings together operational intelligence, advanced analytics,
                            and AI — purpose-built for world-class terminal operations.
                        </p>
                    </div>

                    {/* ── Login card ── */}
                    <div style={{
                        width: '100%',
                        maxWidth: '420px',
                        margin: '0 auto 52px',
                        animation: 'slideUp 0.6s ease-out',
                    }}>
                        <div style={{
                            background: '#FFFFFF',
                            borderRadius: '16px',
                            padding: '40px 40px 36px',
                            boxShadow: '0 2px 8px rgba(0,36,61,0.04), 0 8px 32px rgba(0,36,61,0.06)',
                            border: '1px solid #ECEEF1',
                            display: 'flex',
                            flexDirection: 'column' as const,
                            alignItems: 'center',
                        }}>
                            <div style={{
                                width: 56,
                                height: 56,
                                borderRadius: '50%',
                                backgroundColor: '#F0F6FB',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '24px',
                            }}>
                                <McIcon icon="lock" style={{ color: '#0073AB', width: '24px', height: '24px' }} />
                            </div>

                            <h2 style={{
                                fontSize: '1.4rem',
                                fontWeight: 700,
                                color: '#00243D',
                                margin: '0 0 8px 0',
                                textAlign: 'center',
                            }}>
                                Welcome
                            </h2>
                            <p style={{
                                fontSize: '0.9rem',
                                color: '#7A8B9A',
                                margin: '0 0 32px 0',
                                lineHeight: 1.5,
                                textAlign: 'center',
                            }}>
                                Sign in with your corporate account to continue.
                            </p>

                            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                <McButton
                                    appearance="primary"
                                    fit="large"
                                    onClick={() => login()}
                                >
                                    Access for Employees
                                </McButton>
                            </div>

                            <div style={{
                                marginTop: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                            }}>
                                <McIcon icon="shield" style={{ width: '13px', height: '13px', color: '#C0C9D2' }} />
                                <span style={{ fontSize: '0.74rem', color: '#C0C9D2' }}>
                                    Protected by enterprise single sign-on
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* ── Platform capabilities ── */}
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: '24px',
                        maxWidth: '720px',
                        margin: '0 auto',
                    }}>
                        {pillars
                            .filter((p) => {
                                // if pillar requires a role, check it via auth
                                if (!p.roleKey) return true;
                                return hasRole(getRole(p.roleKey as RoleKey));
                            })
                            .map((p, i) => (
                            <div
                                key={i}
                                className="lp-pillar"
                                role={p.path ? 'button' : undefined}
                                tabIndex={p.path ? 0 : undefined}
                                onClick={() => p.path && navigate(p.path)}
                                onKeyDown={(e) => {
                                    if ((e.key === 'Enter' || e.key === ' ') && p.path) {
                                        e.preventDefault();
                                        navigate(p.path);
                                    }
                                }}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column' as const,
                                    alignItems: 'center',
                                    gap: '10px',
                                    animation: `slideUp ${0.5 + i * 0.06}s ease-out`,
                                    cursor: p.path ? 'pointer' : 'default',
                                    width: '100px',
                                }}
                            >
                                <div style={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: '50%',
                                    background: `${p.accent}0F`,
                                    border: `1.5px solid ${p.accent}22`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                    <img
                                        src={`/assets/icons/${p.icon}`}
                                        alt={p.title}
                                        style={{ height: 24, width: 24, opacity: 0.85 }}
                                    />
                                </div>
                                <span style={{
                                    fontSize: '0.72rem',
                                    fontWeight: 600,
                                    color: '#3D4F5F',
                                    textAlign: 'center',
                                    lineHeight: 1.3,
                                }}>
                                    {p.title}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ━━━━━━━━━━ FOOTER ━━━━━━━━━━ */}
            <div style={{
                borderTop: '1px solid #F0F1F3',
                padding: '14px 40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: '#FFFFFF',
                flexWrap: 'wrap',
                gap: '8px',
            }}>
                <span style={{ fontSize: '0.72rem', color: '#B8C4CE' }}>
                    © {new Date().getFullYear()} APM Terminals. All rights reserved.
                </span>
                <span style={{ fontSize: '0.72rem', color: '#B8C4CE' }}>
                    CDH Enterprise
                </span>
            </div>
        </div>
    );
};

export default LandingPage;
